// src/components/contact/ContactForm.jsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContactCard,
  Header,
  Ribbon,
  Subtitle,
  Title,
  OrnDivider,
  Form,
  Row,
  Field,
  Label,
  Input,
  Textarea,
  Actions,
  SubmitBtn,
  ErrorMsg,
  Divider,
  SuccessMsg,
  Helper,
} from "./ContactFormStyles";
import { gql, useMutation } from "@apollo/client";

const CREATE_CONTACT_MESSAGE = gql`
  mutation CreateContactMessage(
    $name: String!
    $email: String
    $phone: String!
    $subject: String
    $message: String!
    $source_path: String
  ) {
    createContactMessage(
      name: $name
      email: $email
      phone: $phone
      subject: $subject
      message: $message
      source_path: $source_path
    ) {
      message
      id
    }
  }
`;

export default function ContactForm() {
  const { t } = useTranslation();

  const [nume, setNume] = useState("");
  const [email, setEmail] = useState("");
  const [telefon, setTelefon] = useState("");
  const [subiect, setSubiect] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [errors, setErrors] = useState({});
  const [ok, setOk] = useState(false);

  const [mutate, { loading: sending }] = useMutation(CREATE_CONTACT_MESSAGE);

  const validate = () => {
    const e = {};
    if (!nume.trim()) e.nume = t("contact.form.errors.nameRequired");
    if (!telefon.trim()) e.telefon = t("contact.form.errors.phoneRequired");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = t("contact.form.errors.emailInvalid");
    if (!mesaj.trim()) e.mesaj = t("contact.form.errors.messageRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setOk(false);
    if (!validate()) return;

    try {
      const source_path =
        typeof window !== "undefined" ? window.location.pathname : null;

      await mutate({
        variables: {
          name: nume.trim(),
          email: email.trim() || null,
          phone: telefon.trim(),
          subject: subiect.trim() || null,
          message: mesaj.trim(),
          source_path,
        },
      });

      setOk(true);
      setNume("");
      setEmail("");
      setTelefon("");
      setSubiect("");
      setMesaj("");
      setErrors({});
    } catch (err) {
      console.error("Eroare trimitere mesaj:", err?.message || err);
      setErrors({ submit: t("contact.form.submitError") });
    }
  };

  return (
    <ContactCard>
      <Header>
        <Ribbon aria-hidden="true" />
        <Subtitle>{t("contact.form.subtitle")}</Subtitle>
        <Title>{t("contact.form.title")}</Title>
      </Header>

      <OrnDivider $scale={0.9} aria-hidden="true" />

      <Form onSubmit={handleSubmit} noValidate>
        <Row>
          <Field>
            <Label htmlFor="nume">{t("contact.form.name")}*</Label>
            <Input
              id="nume"
              value={nume}
              onChange={(e) => setNume(e.target.value)}
              placeholder={t("contact.form.placeholders.name")}
              autoComplete="name"
              inputMode="text"
              aria-invalid={!!errors.nume}
              aria-describedby={errors.nume ? "err-nume" : undefined}
            />
            {errors.nume && <ErrorMsg id="err-nume">{errors.nume}</ErrorMsg>}
          </Field>

          <Field>
            <Label htmlFor="email">{t("contact.form.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("contact.form.placeholders.email")}
              autoComplete="email"
              inputMode="email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "err-email" : undefined}
            />
            {errors.email && <ErrorMsg id="err-email">{errors.email}</ErrorMsg>}
          </Field>
        </Row>

        <Row>
          <Field>
            <Label htmlFor="telefon">{t("contact.form.phone")}*</Label>
            <Input
              id="telefon"
              value={telefon}
              onChange={(e) => setTelefon(e.target.value)}
              placeholder={t("contact.form.placeholders.phone")}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              aria-invalid={!!errors.telefon}
              aria-describedby={errors.telefon ? "err-tel" : undefined}
            />
            {errors.telefon && (
              <ErrorMsg id="err-tel">{errors.telefon}</ErrorMsg>
            )}
            <Helper>{t("contact.form.helperPhone")}</Helper>
          </Field>

          <Field>
            <Label htmlFor="subiect">{t("contact.form.subject")}</Label>
            <Input
              id="subiect"
              value={subiect}
              onChange={(e) => setSubiect(e.target.value)}
              placeholder={t("contact.form.placeholders.subject")}
              autoComplete="off"
            />
          </Field>
        </Row>

        <Field>
          <Label htmlFor="mesaj">{t("contact.form.message")}*</Label>
          <Textarea
            id="mesaj"
            rows={5}
            value={mesaj}
            onChange={(e) => setMesaj(e.target.value)}
            placeholder={t("contact.form.placeholders.message")}
            aria-invalid={!!errors.mesaj}
            aria-describedby={errors.mesaj ? "err-msg" : undefined}
          />
          {errors.mesaj && <ErrorMsg id="err-msg">{errors.mesaj}</ErrorMsg>}
        </Field>

        <Divider aria-hidden />

        <Actions>
          <SubmitBtn type="submit" disabled={sending} aria-busy={sending}>
            {sending
              ? t("contact.form.actions.sending")
              : t("contact.form.actions.submit")}
            {!sending && (
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
              >
                <path
                  d="M5 12h12M13 6l6 6-6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </SubmitBtn>
        </Actions>

        {ok && (
          <SuccessMsg role="status" aria-live="polite">
            {t("contact.form.submitSuccess")}
          </SuccessMsg>
        )}
        {errors.submit && (
          <p style={{ marginTop: 12, color: "#b22" }}>{errors.submit}</p>
        )}
      </Form>
    </ContactCard>
  );
}
